/**
 * Browser-agnostic "is audible autoplay blocked right now?" probe — the SHELL-level signal used
 * before a `<LiveKitRoom>` exists (by which point the gesture decision is already made). A pure
 * classifier (`classifyAutoplayPolicy`, unit-tested) + an impure async wrapper (`probeAudioBlocked`),
 * matching the ptt-state / mic-availability convention.
 *
 * DEFAULT-TO-BLOCKED: on any uncertainty we return `true`. Showing the unlock gesture to a few users
 * who didn't need it is harmless; assuming "allowed" when it wasn't strands them with a silent dealer.
 */

/**
 * The values `navigator.getAutoplayPolicy('mediaelement')` can return (Chrome-only).
 * - `allowed`       : audible autoplay works — nothing to unlock.
 * - `allowed-muted` : only muted autoplay works — audible still needs a gesture.
 * - `disallowed`    : no autoplay — needs a gesture.
 */
export type AutoplayPolicy = 'allowed' | 'allowed-muted' | 'disallowed';

/**
 * Map an autoplay policy to "is audible audio blocked?" (`allowed-muted` counts as blocked).
 * `undefined` → `null` (no signal; the caller picks the safe default).
 */
export function classifyAutoplayPolicy(policy: AutoplayPolicy | undefined): boolean | null {
  if (policy === undefined) {
    return null;
  }
  return policy !== 'allowed';
}

/**
 * The autoplay policy as a blocked-verdict: `true` blocked, `false` allowed, `null` when the API is
 * absent (Safari/WebKit — only Chromium exposes `getAutoplayPolicy`).
 */
export function getAutoplayPolicyVerdict(): boolean | null {
  const nav = navigator as Navigator & { getAutoplayPolicy?: (type: string) => AutoplayPolicy };
  if (typeof nav.getAutoplayPolicy !== 'function') {
    return null;
  }
  return classifyAutoplayPolicy(nav.getAutoplayPolicy('mediaelement'));
}

// A 44-byte silent WAV as a data URI — playing it produces no audible sound, so the fallback probe
// never leaks a click even when autoplay IS allowed.
const SILENT_WAV_DATA_URI =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

/**
 * Resolve whether audible autoplay is blocked right now: the Chrome policy if definite, else a
 * silent-`<audio>`-play probe, else blocked (the safe default).
 */
export async function probeAudioBlocked(): Promise<boolean> {
  // 1. Chrome's authoritative autoplay policy.
  try {
    const verdict = getAutoplayPolicyVerdict();
    if (verdict !== null) {
      return verdict;
    }
  } catch {
    // Fall through to the playback probe.
  }

  // 2. Silent-audio probe for every other engine: play() resolving = allowed, rejection = blocked.
  let el: HTMLAudioElement | undefined;
  try {
    el = new Audio(SILENT_WAV_DATA_URI);
    el.muted = false;
    el.volume = 0;
    const playback = el.play();
    if (playback && typeof playback.then === 'function') {
      await playback;
    }
    el.pause();
    return false;
  } catch {
    return true; // blocked (the safe default)
  } finally {
    if (el) {
      try {
        el.pause();
        el.removeAttribute('src');
        el.load();
      } catch {
        // best-effort cleanup
      }
    }
  }
}
